import { COUNTRIES } from "@breenova/domain";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FieldHint,
  Input,
  Label,
  Picker,
} from "@breenova/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";

import { useAuth } from "@/auth/auth-context";
import { DetailHeader } from "@/components/app-shell";
import { useUpdateProfile } from "@/lib/account";
import { api } from "@/lib/api";
import { SettingsFrame } from "./settings-frame";

interface FederationOption {
  id: string;
  code: string;
  name: string;
  countryCode?: string | null;
}

/**
 * The breeder's own details.
 *
 * The same four fields registration collected, plus the time zone every date on every
 * screen is rendered through. Email is shown but not editable: it is the login identity,
 * so changing it means proving the new address is reachable before the old one stops
 * working, and a field that can silently lock someone out of their own aviary is worse
 * than no field.
 */
export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const update = useUpdateProfile();

  const [displayName, setDisplayName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [federationId, setFederationId] = useState("");
  const [ringCode, setRingCode] = useState("");
  const [timeZoneId, setTimeZoneId] = useState("");
  const [saved, setSaved] = useState(false);

  // Seeded from the loaded profile. Keyed on the breeder so switching accounts in one
  // session cannot leave the previous breeder's details in the form.
  useEffect(() => {
    if (!user) {
      return;
    }

    setDisplayName(user.displayName);
    setCountryCode(user.countryCode ?? "");
    setFederationId(user.federationId ?? "");
    setRingCode(user.ringCode ?? "");
    setTimeZoneId(user.timeZoneId);
  }, [user]);

  const federations = useQuery({
    queryKey: ["registration-options"],
    queryFn: async (): Promise<FederationOption[]> => {
      const { data, error } = await api.GET("/api/v1/auth/registration-options");

      if (error || !data) {
        throw new Error("Could not load federations");
      }

      return (data.federations ?? []) as FederationOption[];
    },
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Federations are mostly national, so the ones for the chosen country come first. A
  // membership already recorded stays listed even if the country no longer matches —
  // otherwise moving country silently drops the federation from the form.
  const relevantFederations = (federations.data ?? []).filter(
    (federation) =>
      !countryCode ||
      !federation.countryCode ||
      federation.countryCode === countryCode ||
      federation.id === federationId,
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaved(false);

    await update.mutateAsync({
      displayName,
      countryCode: countryCode || null,
      federationId: federationId || null,
      ringCode: ringCode || null,
      timeZoneId: timeZoneId || null,
    });

    // The shell shows the breeder's name, so it has to hear about the change too.
    await refreshUser();
    setSaved(true);
  }

  return (
    <div className="flex flex-col gap-md md:gap-lg">
      <DetailHeader backTo="/more" kicker="Your aviary" title="Breeder details" />

      <SettingsFrame>
        <section className="flex w-full flex-col gap-md">
          <Card>
            <CardContent>
              <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-md" noValidate>
                {update.isError ? (
                  <Alert tone="error">
                    {update.error instanceof Error
                      ? update.error.message
                      : "Could not save your details."}
                  </Alert>
                ) : null}

                {saved && !update.isPending ? <Alert tone="success">Saved.</Alert> : null}

                <Field>
                  <Label htmlFor="displayName">Name</Label>
                  <Input
                    id="displayName"
                    name="displayName"
                    autoComplete="name"
                    required
                    value={displayName}
                    onChange={(event) => {
                      setDisplayName(event.target.value);
                      setSaved(false);
                    }}
                  />
                  <FieldHint>Printed on pedigree certificates and provenance reports.</FieldHint>
                </Field>

                <Field>
                  <Label>Country</Label>
                  <Picker
                    id="countryCode"
                    label="Country"
                    value={countryCode}
                    onChange={(value) => {
                      setCountryCode(value);
                      setSaved(false);
                    }}
                    options={[
                      { value: "", label: "Not set" },
                      ...COUNTRIES.map((country) => ({
                        value: country.code,
                        label: country.name,
                      })),
                    ]}
                  />
                </Field>

                <Field>
                  <Label>Federation</Label>
                  <Picker
                    id="federationId"
                    label="Federation"
                    disabled={federations.isPending}
                    emptyLabel="No federation for that country"
                    value={federationId}
                    onChange={(value) => {
                      setFederationId(value);
                      setSaved(false);
                    }}
                    options={[
                      { value: "", label: "Not a member" },
                      ...relevantFederations.map((federation) => ({
                        value: federation.id,
                        label: federation.name,
                        hint: federation.code,
                      })),
                    ]}
                  />
                  <FieldHint>Sets the ring format your birds are checked against.</FieldHint>
                </Field>

                <Field>
                  <Label htmlFor="ringCode">Ring code</Label>
                  <Input
                    id="ringCode"
                    name="ringCode"
                    placeholder="NL-1234"
                    value={ringCode}
                    onChange={(event) => {
                      setRingCode(event.target.value);
                      setSaved(false);
                    }}
                  />
                  <FieldHint>The breeder code stamped on your closed rings.</FieldHint>
                </Field>

                <Field>
                  <Label>Time zone</Label>
                  <Picker
                    id="timeZoneId"
                    label="Time zone"
                    value={timeZoneId}
                    onChange={(value) => {
                      setTimeZoneId(value);
                      setSaved(false);
                    }}
                    options={timeZones(timeZoneId).map((zone) => ({
                      value: zone,
                      label: zone,
                    }))}
                  />
                  <FieldHint>
                    Every date is stored in UTC and shown in this zone. Get it wrong and a
                    hatch date can read a day out.
                  </FieldHint>
                </Field>

                <Button type="submit" disabled={update.isPending} className="self-start">
                  {update.isPending ? "Saving…" : "Save changes"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Email</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-sm">
              <p className="text-body text-on-surface">{user?.email ?? "—"}</p>
              <p className="text-body text-on-surface-variant">
                This is the address you sign in with. Changing it means confirming the new
                one before the old one stops working, so it is not a field here yet.
              </p>
            </CardContent>
          </Card>
        </section>
      </SettingsFrame>
    </div>
  );
}

/**
 * The zones this browser knows, with the breeder's current one guaranteed to be present.
 *
 * `supportedValuesOf` is missing on older WebViews, which a Capacitor build will meet.
 * Falling back to just the stored zone plus the device's own keeps the form usable and,
 * more importantly, keeps it from silently rewriting a zone it could not list.
 */
function timeZones(current: string): string[] {
  const device = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const known =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [device, "UTC"];

  return [...new Set([current, ...known].filter(Boolean))].sort((a, b) => a.localeCompare(b));
}
